<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import { fetchAccountProductsConfig, sampleCreateProductsForAccount } from '@/api/accountsAdmin'

const props = defineProps<{
  show: boolean
  accountJuid: string
  productPath: string
  accountFriendlyName?: string
}>()

const emit = defineEmits(['close', 'success'])

const modal = ref(props.show)
const loading = ref(false)

const config = ref<Record<string, any>>({})
const productFriendlyName = ref('')
const sampleObj = ref<Record<string, any>>({})
const commonData = ref<Record<string, any>>({})
const formData = reactive<Record<string, any>>({})
const validationErrors = ref<string[]>([])

const snackbar = ref({
  show: false,
  message: '' as string,
  color: 'error' as 'error' | 'success' | 'info',
})

const patternFieldOverrides: Record<string, { label: string; placeholder: string }> = {
  import_locate: { label: 'Dealer PA', placeholder: 'e.g. 05534' },
  import_fdds: { label: 'Dealer PA', placeholder: 'e.g. 05534' },
  import_fdds1: { label: 'Dealer PA', placeholder: 'e.g. 05534' },
  import_fdds2: { label: 'Dealer PA', placeholder: 'e.g. 05534' },
  import_homenet: { label: 'Dealer ID', placeholder: 'e.g. HarteFamilyMotors' },
  import_homenet_api: { label: 'Dealer ID', placeholder: 'e.g. HarteFamilyMotors' },
}

const getFieldLabel = (key: string): string => {
  if (key === 'pattern') {
    const feedName = props.productPath.split('/').pop() || props.productPath
    return patternFieldOverrides[feedName]?.label || 'Pattern'
  }
  return key
}

const getFieldPlaceholder = (key: string): string => {
  if (key === 'pattern') {
    const feedName = props.productPath.split('/').pop() || props.productPath
    return patternFieldOverrides[feedName]?.placeholder || 'e.g. MP24196'
  }
  return ''
}

const initializeFormData = (sample: Record<string, any>) => {
  Object.keys(sample).forEach((key) => {
    if (Array.isArray(sample[key])) {
      formData[key] = sample[key].map((item: any) =>
        typeof item === 'object' ? { ...item } : item
      )
    } else if (typeof sample[key] === 'object') {
      formData[key] = { ...sample[key] }
    } else {
      formData[key] = sample[key]
    }
  })
}

const isFieldRequired = (key: string, subKey?: string): boolean => {
  if (subKey) {
    return config.value.properties[key]?.items?.required?.includes(subKey) ?? false
  }
  return config.value.required?.includes(key) ?? false
}

const hasRequiredNestedFields = (key: string): boolean => {
  return (config.value.properties[key]?.required ?? []).length > 0
}

const getRequiredNestedFields = (key: string): string[] => {
  return config.value.properties[key]?.required ?? []
}

const validateForm = (): boolean => {
  validationErrors.value = []

  for (const key of config.value.required || []) {
    const value = formData[key]

    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      validationErrors.value.push(`Field "${key}" is required.`)
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      for (const subKey of config.value.properties[key]?.required || []) {
        if (value[subKey] === undefined || value[subKey] === null || value[subKey] === '') {
          validationErrors.value.push(`Field "${key}.${subKey}" is required.`)
        }
      }
    }
  }

  return validationErrors.value.length === 0
}

const addArrayItem = (key: string) => {
  if (Array.isArray(formData[key])) {
    formData[key].push(config.value?.properties[key]?.items?.type === 'object' ? {} : '')
  }
}

const removeArrayItem = (key: string, index: number) => {
  if (Array.isArray(formData[key])) {
    formData[key].splice(index, 1)
  }
}

const onNumberInput = (key: string) => {
  const value = formData[key]
  if (value !== '' && !isNaN(value)) {
    formData[key] = Number(value)
  } else {
    formData[key] = ''
  }
}

const onNumberInput2 = (key: string, index: number) => {
  const value = formData[key][index]
  if (typeof value === 'string') {
    const parsedValue = parseFloat(value)
    if (!isNaN(parsedValue)) {
      formData[key][index] = parsedValue
    } else {
      formData[key][index] = ''
    }
  }
}

const enforceNonEmptyNonNegativeNumber = (key: string, index: number, subKey: string) => {
  let value = formData[key][index][subKey]
  if (typeof value === 'string') {
    value = parseFloat(value)
  }
  if (value === '' || value === null || isNaN(value)) {
    formData[key][index][subKey] = 0
  }
  if (value < 0) {
    formData[key][index][subKey] = 0
  }
}

// Non-mutating check to inform CTA label
const isFormValid = computed(() => {
  try {
    const requiredTop: string[] = Array.isArray(config.value?.required) ? config.value.required : []
    for (const key of requiredTop) {
      const value = (formData as any)[key]
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return false
      }
      if (typeof value === 'object' && !Array.isArray(value)) {
        const nestedReq: string[] = config.value?.properties?.[key]?.required || []
        for (const subKey of nestedReq) {
          const v = value[subKey]
          if (v === undefined || v === null || v === '') {
            return false
          }
        }
      }
    }
    return true
  } catch {
    return true
  }
})

const loadingError = ref<string>('')

const loadProductConfig = async () => {
  if (!props.productPath) return
  loadingError.value = ''
  try {
    const data = await fetchAccountProductsConfig(props.productPath)
    config.value = data.config
    productFriendlyName.value =
      data?.commonData?.productFriendlyName ||
      data?.config?.productFriendlyName ||
      data?.config?.productName ||
      ''
    sampleObj.value = data.sampleObj
    initializeFormData(data.sampleObj)
    commonData.value = data.commonData || {}
  } catch (error: any) {
    console.error('Failed to load product config:', error)
    loadingError.value =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load product configuration. Please check your network connection and try again.'
    // Set empty sampleObj to show error message instead of loading
    sampleObj.value = {}
  }
}

const close = () => {
  modal.value = false
  emit('close')
}

const submitForm = async () => {
  if (!validateForm()) return

  const objToSend = {
    accountName: props.accountJuid,
    productName: props.productPath,
    config: JSON.parse(JSON.stringify(formData)),
    commonData: commonData.value,
  }

  try {
    loading.value = true
    await sampleCreateProductsForAccount(objToSend)
    snackbar.value.message = 'Product for Account created successfully!'
    snackbar.value.color = 'success'
    snackbar.value.show = true
    setTimeout(() => {
      loading.value = false
      emit('success')
      close()
    }, 600)
  } catch (error: any) {
    console.error('Error creating Product:', error)
    alert(error?.response?.data || 'Failed to create product')
    loading.value = false
  }
}

watch(
  () => props.show,
  (val) => {
    modal.value = val
    if (val) loadProductConfig()
  },
  { immediate: true }
)

watch(
  () => props.productPath,
  () => {
    if (modal.value) loadProductConfig()
  }
)
</script>

<template>
  <v-dialog
    v-model="modal"
    :max-width="$vuetify.display.mdAndDown ? '95%' : '900px'"
    persistent
    data-testid="add-product-dialog"
  >
    <v-card>
      <v-toolbar color="primary" dark>
        <v-toolbar-title>Confirm Product Add</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text>
        <div class="text-body-1 mb-4">
          Are you sure you want to add
          <strong>{{ productFriendlyName || props.productPath }}</strong>
          to
          <strong>{{ props.accountFriendlyName || props.accountJuid }}</strong>
          ?
        </div>
        <form @submit.prevent="submitForm" v-if="Object.keys(sampleObj).length">
          <div v-for="(value, key) in sampleObj" :key="key" class="mb-6">
            <div v-if="Array.isArray(value)">
              <label
                class="block text-lg font-medium mb-2"
                :class="isFieldRequired(key) ? 'required-label' : ''"
              >
                {{ key }}
                <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
              </label>
              <p v-if="hasRequiredNestedFields(key)" class="text-gray-500 text-sm">
                This array contains required fields: {{ getRequiredNestedFields(key).join(', ') }}
              </p>

              <div v-if="typeof value[0] === 'string'">
                <div
                  v-for="(item, index) in formData[key]"
                  :key="index"
                  class="flex flex-col gap-2 mb-2"
                >
                  <v-text-field
                    v-model="formData[key][index]"
                    :label="`Item ${index + 1}`"
                    type="text"
                    outlined
                    dense
                  />
                  <v-btn icon color="error" @click="removeArrayItem(key, index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>

              <div v-else-if="typeof value[0] === 'number'">
                <div
                  v-for="(item, index) in formData[key]"
                  :key="index"
                  class="flex flex-col gap-2 mb-2"
                >
                  <v-text-field
                    v-model="formData[key][index]"
                    :label="`Item ${index + 1}`"
                    type="number"
                    outlined
                    dense
                    @input="onNumberInput2(key, index)"
                  />
                  <v-btn icon color="error" @click="removeArrayItem(key, index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>

              <div v-else-if="typeof value[0] === 'object'">
                <div
                  v-for="(item, index) in formData[key]"
                  :key="index"
                  class="flex flex-col gap-2 mb-2"
                >
                  <div v-for="(subValue, subKey) in item" :key="subKey" class="flex flex-col gap-2">
                    <label
                      class="block text-sm font-medium"
                      :class="isFieldRequired(key, subKey) ? 'required-label' : ''"
                    >
                      {{ subKey }}
                      <span v-if="isFieldRequired(key, subKey)" class="text-red-500">*</span>
                    </label>
                    <v-select
                      v-if="typeof subValue === 'boolean'"
                      v-model="formData[key][index][subKey]"
                      :items="[true, false]"
                      :item-title="(i) => (i ? 'True' : 'False')"
                      :item-value="(i) => i"
                      label="Select True/False"
                      outlined
                      dense
                      :color="isFieldRequired(key, subKey) ? 'error' : undefined"
                    />
                    <v-text-field
                      v-else-if="typeof subValue === 'number'"
                      v-model.number="formData[key][index][subKey]"
                      type="number"
                      outlined
                      dense
                      @blur="enforceNonEmptyNonNegativeNumber(key, index, subKey)"
                      :color="isFieldRequired(key, subKey) ? 'error' : undefined"
                    />
                    <v-text-field
                      v-else
                      v-model="formData[key][index][subKey]"
                      outlined
                      dense
                      :color="isFieldRequired(key, subKey) ? 'error' : undefined"
                    />
                  </div>
                </div>
              </div>

              <v-btn
                v-if="config?.properties[key]?.items?.type !== 'object'"
                type="button"
                @click="addArrayItem(key)"
                class="mt-2"
              >
                Add Item
              </v-btn>
            </div>

            <div v-else-if="typeof value === 'string'">
              <label
                class="block text-lg font-medium mb-2"
                :class="isFieldRequired(key) ? 'required-label' : ''"
              >
                {{ getFieldLabel(key) }}
                <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
              </label>
              <v-text-field
                v-model="formData[key]"
                outlined
                dense
                :placeholder="getFieldPlaceholder(key)"
                :color="isFieldRequired(key) ? 'error' : undefined"
              />
            </div>

            <div v-else-if="typeof value === 'number'">
              <label
                class="block text-lg font-medium mb-2"
                :class="isFieldRequired(key) ? 'required-label' : ''"
              >
                {{ key }}
                <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
              </label>
              <v-text-field
                v-model="formData[key]"
                type="number"
                outlined
                dense
                @input="onNumberInput(key)"
                :color="isFieldRequired(key) ? 'error' : undefined"
              />
            </div>

            <div v-else-if="key !== 'Enable' && typeof value === 'boolean'">
              <label
                class="block text-lg font-medium mb-2"
                :class="isFieldRequired(key) ? 'required-label' : ''"
              >
                {{ key }}
                <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
              </label>
              <v-select
                v-model="formData[key]"
                :items="[true, false]"
                :item-title="(i) => (i ? 'True' : 'False')"
                :item-value="(i) => i"
                outlined
                dense
                :color="isFieldRequired(key) ? 'error' : undefined"
              />
            </div>

            <div v-else-if="typeof value === 'object'">
              <label
                class="block text-lg font-medium mb-2"
                :class="isFieldRequired(key) ? 'required-label' : ''"
              >
                {{ key }}
                <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
              </label>
              <p v-if="hasRequiredNestedFields(key)" class="text-gray-500 text-sm">
                This object contains required fields: {{ getRequiredNestedFields(key).join(', ') }}
              </p>

              <div class="grid gap-2">
                <div v-for="(subValue, subKey) in value" :key="subKey" class="flex flex-col gap-2">
                  <label
                    class="block text-sm font-medium"
                    :class="isFieldRequired(key, subKey) ? 'required-label' : ''"
                  >
                    {{ subKey }}
                    <span v-if="isFieldRequired(key, subKey)" class="text-red-500">*</span>
                  </label>
                  <v-select
                    v-if="typeof subValue === 'boolean'"
                    v-model="formData[key][subKey]"
                    :items="[true, false]"
                    :item-title="(i) => (i ? 'True' : 'False')"
                    :item-value="(i) => i"
                    label="Select True/False"
                    outlined
                    dense
                    :color="isFieldRequired(key, subKey) ? 'error' : undefined"
                  />
                  <v-text-field
                    v-else-if="typeof subValue === 'number'"
                    v-model.number="formData[key][subKey]"
                    type="number"
                    outlined
                    dense
                    :color="isFieldRequired(key, subKey) ? 'error' : undefined"
                  />
                  <v-text-field
                    v-else
                    v-model="formData[key][subKey]"
                    outlined
                    dense
                    :color="isFieldRequired(key, subKey) ? 'error' : undefined"
                  />
                </div>
              </div>
            </div>
          </div>

          <v-sheet
            class="d-flex align-center"
            elevation="2"
            style="
              position: sticky;
              bottom: 0;
              background: white;
              padding: 10px 12px;
              z-index: 3;
              border-top: 1px solid rgba(0, 0, 0, 0.08);
            "
          >
            <v-spacer></v-spacer>
            <v-btn
              variant="text"
              color="grey-darken-1"
              class="mr-2"
              data-testid="add-product-dialog-cancel"
              @click="close"
            >
              Cancel
            </v-btn>
            <v-btn
              type="submit"
              :disabled="loading || !isFormValid"
              color="primary"
              variant="flat"
              prepend-icon="mdi-check"
              data-testid="add-product-dialog-confirm"
            >
              Confirm
            </v-btn>
          </v-sheet>

          <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top>
            {{ snackbar.message }}
            <template #actions>
              <v-btn text @click="snackbar.show = false">Close</v-btn>
            </template>
          </v-snackbar>
        </form>
        <div v-else-if="loadingError" class="pa-4">
          <v-alert type="error" variant="tonal" density="comfortable">
            <div class="text-body-2">
              <strong>Error loading product configuration</strong>
              <p class="mt-2">{{ loadingError }}</p>
            </div>
          </v-alert>
          <div class="d-flex justify-end mt-4">
            <v-btn variant="text" color="grey" @click="close">Close</v-btn>
            <v-btn color="primary" @click="loadProductConfig" class="ml-2">Retry</v-btn>
          </div>
        </div>
        <p v-else>Loading...</p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
